// Bloom UI-Bausteine für das Admin-Panel
// Basiert auf adminTheme.ts Tokens (Salon-Variante: Blush, Cormorant Garamond, Outfit)
import React from 'react';
import { T, rgba, statusColors } from '../../../utils/adminTheme';

// ─── BloomCard ────────────────────────────────────────────────────────────────
export const BloomCard: React.FC<{
  children: React.ReactNode;
  title?: string;
  action?: React.ReactNode;
  noPad?: boolean;
  style?: React.CSSProperties;
}> = ({ children, title, action, noPad, style }) => (
  <div style={{
    background: T.surface,
    border: `1px solid ${T.line}`,
    borderRadius: T.r.md,
    ...style,
  }}>
    {title && (
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 20px',
        borderBottom: `1px solid ${T.lineSoft}`,
      }}>
        <div style={{
          fontFamily: T.display, fontStyle: 'italic',
          fontSize: 19, color: T.text, fontWeight: 400,
        }}>{title}</div>
        {action}
      </div>
    )}
    <div style={{ padding: noPad ? 0 : '20px 24px' }}>
      {children}
    </div>
  </div>
);

// ─── SectionTitle ─────────────────────────────────────────────────────────────
export const SectionTitle: React.FC<{
  children: React.ReactNode;
  sub?: string;
  action?: React.ReactNode;
}> = ({ children, sub, action }) => (
  <div style={{
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    gap: 12, marginBottom: 18,
  }}>
    <div>
      <div style={{
        fontFamily: T.display, fontStyle: 'italic',
        fontSize: 28, color: T.text, fontWeight: 500,
        letterSpacing: '-0.015em', lineHeight: 1.1,
      }}>{children}</div>
      {sub && (
        <div style={{ fontFamily: T.body, fontSize: 12, color: T.muted, marginTop: 4 }}>{sub}</div>
      )}
    </div>
    {action}
  </div>
);

// ─── BloomButton ──────────────────────────────────────────────────────────────
type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';

export const BloomButton: React.FC<{
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: BtnVariant;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  style?: React.CSSProperties;
  icon?: React.ReactNode;
}> = ({ children, onClick, variant = 'ghost', size = 'md', disabled, type, style, icon }) => {
  const pads = { sm: '5px 12px', md: '8px 16px', lg: '11px 22px' };
  const fonts = { sm: 12, md: 13, lg: 14 };

  const looks: Record<BtnVariant, React.CSSProperties> = {
    primary:   { background: T.accent, color: '#fff', border: `1px solid ${T.accent}` },
    secondary: { background: T.surface2, color: T.text, border: `1px solid ${T.line}` },
    ghost:     { background: 'transparent', color: T.text, border: '1px solid transparent' },
    outline:   { background: 'transparent', color: T.text, border: `1px solid ${T.line}` },
    danger:    { background: 'transparent', color: T.danger, border: `1px solid ${T.line}` },
  };

  return (
    <button
      type={type || 'button'}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: pads[size], fontSize: fonts[size],
        fontFamily: T.body, fontWeight: 500,
        borderRadius: T.r.sm,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all .15s',
        opacity: disabled ? 0.45 : 1,
        whiteSpace: 'nowrap',
        ...looks[variant],
        ...style,
      }}
    >
      {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
      {children}
    </button>
  );
};

// ─── BloomBadge ───────────────────────────────────────────────────────────────
export const BloomBadge: React.FC<{
  children: React.ReactNode;
  fg?: string;
  bg?: string;
  dot?: boolean;
  style?: React.CSSProperties;
}> = ({ children, fg, bg, dot, style }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '3px 9px',
    background: bg || T.surface2,
    color: fg || T.muted,
    border: `1px solid ${bg ? 'transparent' : T.line}`,
    borderRadius: 999,
    fontFamily: T.body, fontSize: 11, fontWeight: 500, letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    ...style,
  }}>
    {dot && (
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: fg || T.muted, flex: '0 0 auto' }} />
    )}
    {children}
  </span>
);

// ─── StatusBadge ──────────────────────────────────────────────────────────────
export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const c = statusColors(status);
  return (
    <BloomBadge fg={c.fg} bg={c.bg} dot>{c.label}</BloomBadge>
  );
};

// ─── BloomInput ───────────────────────────────────────────────────────────────
export const BloomInput: React.FC<{
  label?: string;
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  icon?: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ label, value, onChange, placeholder, type = 'text', icon, style }) => (
  <label style={{ display: 'block', position: 'relative', ...style }}>
    {label && (
      <div style={{
        fontFamily: T.body, fontSize: 11, color: T.muted,
        letterSpacing: '0.02em', marginBottom: 4,
      }}>{label}</div>
    )}
    <div style={{ position: 'relative' }}>
      {icon && (
        <div style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: T.muted, display: 'flex',
        }}>{icon}</div>
      )}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '8px 12px',
          paddingLeft: icon ? 32 : 12,
          background: T.surface, color: T.text,
          border: `1px solid ${T.line}`, borderRadius: T.r.sm,
          fontFamily: T.body, fontSize: 13, outline: 'none',
          transition: 'border .15s', boxSizing: 'border-box',
        }}
        onFocus={e => (e.target.style.borderColor = T.accent)}
        onBlur={e => (e.target.style.borderColor = T.line)}
      />
    </div>
  </label>
);

// ─── BloomSelect ──────────────────────────────────────────────────────────────
export const BloomSelect: React.FC<{
  label?: string;
  value: string | number;
  onChange: (value: string) => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ label, value, onChange, children, style }) => (
  <label style={{ display: 'block', ...style }}>
    {label && (
      <div style={{
        fontFamily: T.body, fontSize: 11, color: T.muted,
        letterSpacing: '0.02em', marginBottom: 4,
      }}>{label}</div>
    )}
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%', padding: '8px 12px',
        background: T.surface, color: T.text,
        border: `1px solid ${T.line}`, borderRadius: T.r.sm,
        fontFamily: T.body, fontSize: 13, outline: 'none',
        transition: 'border .15s', cursor: 'pointer', boxSizing: 'border-box',
      }}
      onFocus={e => (e.target.style.borderColor = T.accent)}
      onBlur={e => (e.target.style.borderColor = T.line)}
    >
      {children}
    </select>
  </label>
);

// ─── BloomTabs ────────────────────────────────────────────────────────────────
export const BloomTabs: React.FC<{
  items: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
}> = ({ items, value, onChange }) => (
  <div style={{
    display: 'inline-flex', gap: 2, padding: 3,
    background: T.surface2, borderRadius: T.r.sm,
    border: `1px solid ${T.line}`,
  }}>
    {items.map(it => (
      <button
        key={it.id}
        onClick={() => onChange(it.id)}
        style={{
          padding: '6px 14px',
          background: value === it.id ? T.surface : 'transparent',
          color: value === it.id ? T.text : T.muted,
          border: 'none', borderRadius: T.r.sm - 2,
          fontFamily: T.body, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          boxShadow: value === it.id ? `0 1px 2px ${rgba(T.text, 0.08)}` : 'none',
          transition: 'all .15s',
          whiteSpace: 'nowrap',
        }}
      >
        {it.label}
      </button>
    ))}
  </div>
);

// ─── BloomEmpty ───────────────────────────────────────────────────────────────
export const BloomEmpty: React.FC<{
  title: string;
  hint?: string;
}> = ({ title, hint }) => (
  <div style={{
    padding: '60px 24px', textAlign: 'center', color: T.muted,
  }}>
    <div style={{
      width: 56, height: 56, borderRadius: '50%',
      background: T.surface2, color: T.accent,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 14, fontSize: 24,
    }}>✦</div>
    <div style={{
      fontFamily: T.display, fontStyle: 'italic',
      fontSize: 18, color: T.text, marginBottom: 6,
    }}>{title}</div>
    {hint && <div style={{ fontFamily: T.body, fontSize: 13 }}>{hint}</div>}
  </div>
);

// ─── BloomToggle ──────────────────────────────────────────────────────────────
export const BloomToggle: React.FC<{
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <div
    onClick={disabled ? undefined : onChange}
    style={{
      width: 44, height: 24, borderRadius: 12,
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: checked ? T.accent : T.faint,
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <div style={{
      position: 'absolute', top: 2, left: checked ? 22 : 2,
      width: 20, height: 20, borderRadius: '50%', background: '#fff',
      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </div>
);

// ─── BloomInfoBox ─────────────────────────────────────────────────────────────
export const BloomInfoBox: React.FC<{
  children: React.ReactNode;
  variant?: 'default' | 'warn' | 'ok';
}> = ({ children, variant = 'default' }) => {
  const colors = {
    default: { bg: rgba(T.info, 0.08), border: rgba(T.info, 0.2), text: T.info },
    warn:    { bg: rgba(T.warn, 0.08), border: rgba(T.warn, 0.2), text: T.warn },
    ok:      { bg: rgba(T.ok,   0.08), border: rgba(T.ok,   0.2), text: T.ok   },
  };
  const c = colors[variant];
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: T.r.md, padding: '12px 16px',
      fontFamily: T.body, fontSize: 13, color: T.text,
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
};
